import React, { Reducer, useContext, useEffect, useReducer, useRef, useState } from 'react';
import { Box, List, ListItem, ListItemText, Typography } from '@mui/material';
import { Footer } from '../../Components/Footer';
import CardExpansion from './CardExpansion';
import {
  usePreRegRoomAllocationQuery,
  useRoomAssignListingLazyQuery,
} from '../../generated/graphql';
import Loading from '../../Components/Loading/Loading';
import { FabricContextProvider } from '../FloorPlan/FabricContext';
import { PreRegFloorPlan } from '../FloorPlan/PreRegFloorPlan';
import { SocketContext } from '../../Pages/Socket/SocketContext';
import { useSnackBar } from '../../Components/Hooks/useSnackBar';
import SnackBarMsg from '../../Components/SnackBar/SnackBarMsg';

interface Props {
  selectedFloor: any;
  selectedRoom: any;
  choosenRoom: any;
}

interface IAction<TType = string, TPayload = any> {
  type: TType;
  payload: TPayload;
}

const initialState: Props = {
  selectedFloor: '',
  selectedRoom: [],
  choosenRoom: '',
};

const reducer: Reducer<Props, IAction> = (state, action) => {
  switch (action.type) {
    case 'reset':
      return initialState;
    default:
      return { ...state, [action.type]: action.payload };
  }
};

const getErrorMessage = (err: any) =>
  err?.graphQLErrors?.[0]?.message ||
  err?.networkError?.result?.errors?.[0]?.message ||
  err?.clientErrors?.[0]?.message ||
  err?.message ||
  'Room assignment failed';

export const AssignRoom = ({
  activeStep,
  setActiveStep,
  roomSelect,
  setRoomSelect,
  RegistrationDetails,
  UpdateRoomAssign,
}) => {
  const socket: any = useContext(SocketContext);

  const [state, dispatch] = useReducer(reducer, initialState);
  const [FloorListing, setFloorListing] = useState<any[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [assigning, setAssigning] = useState(false);
  // Blocks “first-load” floor selection from running again after refetches.
  const floorsInitializedRef = useRef(false);

  const { snackFunc, snackBarMessage, openSnackBar, close } = useSnackBar();

  const {
    refetch: roomRefetch,
    loading: PreRegRoomAllocationLoadings,
    data: { PreRegRoomAllocation } = { PreRegRoomAllocation: [] },
  } = usePreRegRoomAllocationQuery({
    variables: {
      HotelID: RegistrationDetails?.Booking?.HotelID,
      ArrivalDate: RegistrationDetails?.ArrivalDate,
      RoomTypeID: RegistrationDetails?.RoomTypeID,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
    onCompleted: () => {
      RoomAssignListingQuery({
        variables: {
          HotelID: RegistrationDetails?.Booking?.HotelID,
          StartDate: RegistrationDetails?.ArrivalDate,
          EndDate: RegistrationDetails?.DepartureDate,
          RoomTypeID: RegistrationDetails?.RoomTypeID,
        },
      });
    },
  });

  const [
    RoomAssignListingQuery,
    {
      refetch: roomAssignRefetch,
      loading: RoomAssignListingLoadings,
      data: RoomAssignListing
    },
  ] = useRoomAssignListingLazyQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
    onCompleted: data => {
      const filteredLocation: any[] = [];
      const RoomAssignListings: any = data?.RoomAssignListing;

      if (PreRegRoomAllocation?.length > 0 && RoomAssignListings?.length > 0) {
        const existRoom = JSON.parse(localStorage.getItem('SelectedRoom') as any)?.ID;
        const existRoomLoc = JSON.parse(localStorage.getItem('SelectedRoom') as any)?.Location?.ID;

        console.log('PreRegRoomAllocation >>>', PreRegRoomAllocation)
        console.log('RoomAssignListings >>>', RoomAssignListings)

        PreRegRoomAllocation?.forEach(preRoom => {
          let filteredRoom: any[] = [];
          preRoom?.Room.forEach(room => {
            const checkRoom = RoomAssignListings?.filter(
              r => r?.ID === room?.Room?.ID && r?.RoomAssignmentStatus?.Code === 'VC'
            );
            if (checkRoom?.[0]) {
              filteredRoom.push({ ...checkRoom[0], IsSelected: false });
            }

            console.log('room >>>', room)
          });

          if (existRoom && existRoomLoc === preRoom?.LocationID) {
            const selectedRoom = JSON.parse(localStorage.getItem('SelectedRoom') as any);
            if (selectedRoom?.RoomAssignmentStatus?.Code === 'VC') {
              filteredRoom.push(selectedRoom);
            }
          }

          const validRooms = filteredRoom?.filter(s => s.ID !== undefined);
          if (validRooms?.length > 0) {
            filteredLocation.push({
              Location: preRoom?.Location,
              ID: preRoom?.LocationID,
              ImageURL: preRoom?.ImageURL,
              Room: validRooms,
              TotalRoom: validRooms?.length,
            });
          }
        });

        setFloorListing(filteredLocation);

        // Only seed floor/room selection once. Re-running this after a snack /
        // refetch / parent re-render wipes IsSelected and redraws Fabric.
        if (!floorsInitializedRef.current && filteredLocation.length > 0) {
          floorsInitializedRef.current = true;

          if (existRoom) {
            dispatch({
              type: 'choosenRoom',
              payload: true,
            });
            dispatch({
              type: 'selectedFloor',
              payload: filteredLocation?.find(x => x?.ID === existRoomLoc),
            });
            dispatch({
              type: 'selectedRoom',
              payload: filteredLocation?.find(x => x?.ID === existRoomLoc)?.Room,
            });
          } else {
            dispatch({ type: 'selectedFloor', payload: filteredLocation[0] });
            dispatch({ type: 'selectedRoom', payload: filteredLocation[0]?.Room });
          }
        }
      }
    },
  });

  const handleRoomAssign = () => {
    const selectedRoom = state?.selectedRoom?.filter(x => x.IsSelected === true)[0];
    if (!selectedRoom?.ID) {
      snackFunc('Please select a room', false);
      return;
    }

    if (assigning) return;
    setAssigning(true);

    UpdateRoomAssign({
      variables: {
        BookingID: RegistrationDetails?.Booking?.ID,
        RegistrationID: RegistrationDetails?.ID,
        RoomID: selectedRoom?.ID,
        HotelID: RegistrationDetails?.Booking?.HotelID,
        RoomTypeID: RegistrationDetails?.RoomTypeID,
      },
      onError: (err: any) => {
        setAssigning(false);
        snackFunc(getErrorMessage(err), false);
      },
      onCompleted: (data: any) => {
        setAssigning(false);
        if (data?.UpdateRoomAssignment === true) {
          setRoomSelect(selectedRoom);
          localStorage.setItem('SelectedRoom', JSON.stringify(selectedRoom));
          setActiveStep(activeStep + 1);
        } else {
          snackFunc('Room assignment failed', false);
        }
      },
    }).catch(() => {
      setAssigning(false);
    });
  };

  useEffect(() => {
    if (!socket) return;

    const onNotification = (notification: any) => {
      const IsRoomType = notification?.roomType?.filter(
        (x: string) => x === RegistrationDetails?.RoomTypeID
      );
      // Soft refresh listings only — floorsInitializedRef prevents selection reset.
      if (IsRoomType?.length > 0) {
        roomRefetch({
          HotelID: RegistrationDetails?.Booking?.HotelID,
          ArrivalDate: RegistrationDetails?.ArrivalDate,
          RoomTypeID: RegistrationDetails?.RoomTypeID,
        });
        roomAssignRefetch({
          HotelID: RegistrationDetails?.Booking?.HotelID,
          StartDate: RegistrationDetails?.ArrivalDate,
          EndDate: RegistrationDetails?.DepartureDate,
          RoomTypeID: RegistrationDetails?.RoomTypeID,
        });
      }
    };

    socket.on('notification', onNotification);
    return () => {
      socket.off('notification', onNotification);
    };
  }, [socket, RegistrationDetails, roomRefetch, roomAssignRefetch]);

  // Only the first load may show the full-page loader.
  const showInitialLoader =
    !floorsInitializedRef.current &&
    (PreRegRoomAllocationLoadings || RoomAssignListingLoadings) &&
    FloorListing.length === 0;

  return (
    <>
      {showInitialLoader ? (
        <Loading />
      ) : (
        <>
          <Box>
            <Box width="100%" display="flex">
              <Typography fontSize="14px" fontWeight={600} mb="5px">
                Choose Floor
              </Typography>
            </Box>
            <CardExpansion
              style={{ margin: '0 0 10px 0' }}
              expanded={expanded}
              setExpanded={setExpanded}
              iconDisabled={state?.choosenRoom !== '' || assigning}
              summary={
                <ListItemText
                  sx={{ display: 'block' }}
                  primary={
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography fontSize="12px" style={{ fontWeight: 'bold' }}>
                        {state?.selectedFloor?.Location}
                      </Typography>
                      <Typography fontSize="12px" style={{ fontWeight: 'bold', color: '#00bf0f' }}>
                        {state?.selectedFloor?.TotalRoom} Rooms
                      </Typography>
                    </div>
                  }
                />
              }
            >
              <Box style={{ display: 'block' }}>
                {FloorListing?.map((el: any, index: number) => (
                  <List disablePadding key={el?.ID || index}>
                    <ListItem
                      disablePadding
                      onClick={() => {
                        if (assigning) return;
                        dispatch({ type: 'selectedFloor', payload: el });
                        dispatch({ type: 'selectedRoom', payload: el?.Room });
                        setExpanded(false);
                      }}
                    >
                      <ListItemText
                        primary={
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography fontSize="12px" style={{ fontWeight: 'bold' }}>
                              {el?.Location}
                            </Typography>
                            <Typography
                              fontSize="12px"
                              style={{ fontWeight: 'bold', color: '#00bf0f', paddingRight: '18px' }}
                            >
                              {el?.TotalRoom} Rooms
                            </Typography>
                          </div>
                        }
                      />
                    </ListItem>
                  </List>
                ))}
              </Box>
            </CardExpansion>
            <Box style={{ position: 'fixed', top: '225px', zIndex: -1 }}>
              <Typography fontSize="14px" fontWeight={600} mb="5px">
                Choose Room
              </Typography>
            </Box>
            <Box
              style={{
                position: 'fixed',
                top: '250px',
                width: '85%',
                maxWidth: '770px',
                paddingTop: '5px',
                zIndex: expanded === true ? -1 : 0,
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Box style={{ width: '100%', height: '100%', maxWidth: '750px' }}>
                <FabricContextProvider>
                  <PreRegFloorPlan state={state} dispatch={dispatch} />
                </FabricContextProvider>
              </Box>
            </Box>
          </Box>
          <Footer
            primary={true}
            input={state?.selectedRoom?.filter(x => x.IsSelected === true)?.length > 0}
            inputData={state?.selectedRoom?.filter(x => x.IsSelected === true)[0]?.RoomNo}
            options={[
              {
                name: assigning ? 'Please wait...' : 'Next',
                onClick: handleRoomAssign,
                disabled:
                  assigning ||
                  state?.selectedRoom?.filter(x => x.IsSelected === true)?.length === 0,
              },
            ]}
          />
        </>
      )}

      <SnackBarMsg open={openSnackBar} message={snackBarMessage} onClose={close} />
    </>
  );
};
